import React, { useEffect, useMemo, useState } from "react";
import "./DataEstateWidget.scss";
import { useDispatch, useSelector } from "react-redux";
import { numberFormatter } from "../../../components/common/Dashboard/utils";
import { FETCH_DATA_ESTATE_COVERAGE_CARD } from "../DataTagConstants";

/* ------------------------------------------------------------------ */
/* Icons                                                               */
/* ------------------------------------------------------------------ */
const DATA_SOURCE_ICONS = {
  ONEDRIVE: "/dataSourceIcons/oneDrive_color.svg",
  POSTGRESQL: "/dataSourceIcons/postgre.svg",
  MSSQL: "/dataSourceIcons/mssql.svg",
  SQLSERVER: "/dataSourceIcons/mssql.svg",
  MYSQL: "/dataSourceIcons/mysql.svg",
  DROPBOX: "/dataSourceIcons/dropBoxLogo.svg",
  GOOGLEDRIVE: "/dataSourceIcons/GoogleDriveIcon.svg",
  NTFS: "/dataSourceIcons/ntfsIcon.svg",
  ENTRA: "/dataSourceIcons/microsoft-entra.svg",
  GOOGLEWORKSPACE: "/dataSourceIcons/google-g-icon.svg",
  MICROSOFTAD: "/dataSourceIcons/MSADIcon.svg",
  APPLICATION: "/dataSourceIcons/ApiColor.svg",
  DATASOURCE: "/dataSourceIcons/DatabaseColor.svg",
  OTHER: "/dataSourceIcons/DatabaseColor.svg",
};

/**
 * The coverage API only returns { dataSourceId, dataSourceName, totalFiles, processedFiles }.
 * If the item (or your data-source list) carries a type, pass it as `dataSourceType`.
 * Otherwise we fall back to a name guess (FS* / NTFS* -> NTFS) and then the generic DB icon.
 */
const getIconFor = (item) => {
  const raw = String(item.dataSourceType || item.type || "")
    .toUpperCase()
    .replace(/[\s_-]/g, "");
  if (DATA_SOURCE_ICONS[raw]) return DATA_SOURCE_ICONS[raw];
  if (/^(FS|NTFS)/i.test(item.dataSourceName || "")) return DATA_SOURCE_ICONS.NTFS;
  return DATA_SOURCE_ICONS.DATASOURCE;
};

/* ------------------------------------------------------------------ */
/* Geometry / layout constants (SVG viewBox 360 x 290)                 */
/* ------------------------------------------------------------------ */
const VB_W = 360;
const VB_H = 290;
const CX = 180;
const CY = 158;
const R0 = 38; // inner radius of sectors
const R1 = 84; // outer radius of sectors
const GAUGE_R = 100; // radius of the overall gauge arc
const OFFSET = -Math.PI * 0.75; // gauge starts at 7:30
const SWEEP = Math.PI * 1.5; // 270 degrees
const PAD = 0.018; // gap between sectors (rad)
const MIN_SPAN = (13 * Math.PI) / 180; // smallest sector drawn, so icons and labels never collide
const GAUGE_DONE = "__gauge_done__";
const GAUGE_REST = "__gauge_rest__";
const MAX_SECTORS = 6; // top 5 sources + "Others"

const COLORS = ["#2f3bff", "#7c5cf0", "#0ea5e9", "#0d9488", "#d97706", "#db2777"];
const OTHERS_COLOR = "#64748b";

const point = (r, a) => [CX + r * Math.sin(a), CY - r * Math.cos(a)];
const f2 = (v) => v.toFixed(2);

const annularSector = (r0, r1, a0, a1) => {
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const A = point(r1, a0);
  const B = point(r1, a1);
  const C = point(r0, a1);
  const D = point(r0, a0);
  return `M${f2(A[0])} ${f2(A[1])} A${r1} ${r1} 0 ${large} 1 ${f2(B[0])} ${f2(B[1])} L${f2(C[0])} ${f2(C[1])} A${r0} ${r0} 0 ${large} 0 ${f2(D[0])} ${f2(D[1])} Z`;
};

const gaugeArc = (a0, a1) => {
  const A = point(GAUGE_R, a0);
  const B = point(GAUGE_R, a1);
  return `M${f2(A[0])} ${f2(A[1])} A${GAUGE_R} ${GAUGE_R} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${f2(B[0])} ${f2(B[1])}`;
};

const mix = (hex, t, to) => {
  const c = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `#${c.map((v) => Math.round(v + (to - v) * t).toString(16).padStart(2, "0")).join("")}`;
};

const fmtPct = (v) => {
  if (v > 0 && v < 0.1) return "<0.1%";
  return `${v >= 10 ? Math.round(v) : Math.round(v * 10) / 10}%`;
};

/* ------------------------------------------------------------------ */
/* Turn API rows into drawable sectors                                 */
/* ------------------------------------------------------------------ */
const buildSectors = (rows) => {
  const sorted = [...rows]
    .filter((r) => Number(r.totalFiles) > 0)
    .sort((a, b) => b.totalFiles - a.totalFiles);

  let items = sorted;
  if (sorted.length > MAX_SECTORS) {
    const head = sorted.slice(0, MAX_SECTORS - 1);
    const rest = sorted.slice(MAX_SECTORS - 1);
    items = [
      ...head,
      {
        dataSourceId: "__others__",
        dataSourceName: `Others (${rest.length} sources)`,
        totalFiles: rest.reduce((a, r) => a + r.totalFiles, 0),
        processedFiles: rest.reduce((a, r) => a + r.processedFiles, 0),
        isOthers: true,
      },
    ];
  }

  const all = items.reduce((a, r) => a + r.totalFiles, 0);
  if (!all) return { sectors: [], all: 0, allDone: 0 };
  const allDone = items.reduce((a, r) => a + r.processedFiles, 0);

  // Angles: proportional, but never narrower than MIN_SPAN (labels still show the true %).
  const raw = items.map((r) => (r.totalFiles / all) * SWEEP);
  const small = raw.map((s) => s < MIN_SPAN);
  const bigSum = raw.reduce((a, s, i) => (small[i] ? a : a + s), 0);
  const bigBudget = SWEEP - small.filter(Boolean).length * MIN_SPAN;
  const spans = raw.map((s, i) => (small[i] ? MIN_SPAN : bigSum ? (s * bigBudget) / bigSum : s));

  let start = 0;
  const sectors = items.map((r, i) => {
    const span = spans[i];
    const a0 = OFFSET + start + PAD;
    const a1 = OFFSET + start + span - PAD;
    const mid = OFFSET + start + span / 2;
    start += span;

    const color = r.isOthers ? OTHERS_COLOR : COLORS[i % COLORS.length];
    const doneFrac = r.totalFiles ? r.processedFiles / r.totalFiles : 0;
    const rSplit =
      r.processedFiles <= 0
        ? R0
        : r.processedFiles >= r.totalFiles
          ? R1
          : R0 + Math.max(3, (R1 - R0) * doneFrac);

    return {
      id: r.dataSourceId,
      name: r.dataSourceName,
      color,
      total: r.totalFiles,
      done: r.processedFiles,
      share: (r.totalFiles / all) * 100,
      pct: doneFrac * 100,
      mid,
      doneGrad: [mix(color, 0.12, 0), mix(color, 0.3, 255)],
      restGrad: [mix(color, 0.78, 255), mix(color, 0.9, 255)],
      // processed fills from the centre outwards; the rest is the outer part of the sector
      doneD: r.processedFiles > 0 ? annularSector(R0, rSplit, a0, a1) : "",
      restD: r.processedFiles < r.totalFiles ? annularSector(rSplit, R1, a0, a1) : "",
      icon: r.isOthers ? null : getIconFor(r),
      isOthers: !!r.isOthers,
    };
  });

  return { sectors, all, allDone };
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
export default function DataEstateWidget() {
  const dispatch = useDispatch();
  const cardData = useSelector((state) => state.dataTagReducer.dataEstateCoverageCard);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    dispatch({ type: FETCH_DATA_ESTATE_COVERAGE_CARD });
  }, [dispatch]);

  const rows = useMemo(() => (Array.isArray(cardData) ? cardData : cardData?.dataSources || []), [cardData]);
  const { sectors, all, allDone } = useMemo(() => buildSectors(rows), [rows]);

  const overallPct = all ? (allDone / all) * 100 : 0;
  const current = sectors.find((s) => s.id === hovered);
  const shownPct = overallPct; // always the overall processed %, never changes on hover

  const gaugeFrac = Math.max(0.012, all ? allDone / all : 0);
  const trackD = gaugeArc(OFFSET, OFFSET + SWEEP);
  const doneD = all && allDone > 0 ? gaugeArc(OFFSET, OFFSET + SWEEP * gaugeFrac) : "";

  const onGauge = hovered === GAUGE_DONE || hovered === GAUGE_REST;
  const tipSide = "tip-right"; // always the same side, so it never jumps

  return (
    <div className="data-estate-widget">
      <h4 className="widget-title-data-estate">Data Estate Coverage</h4>

      <div className="de-chart" onMouseLeave={() => setHovered(null)}>
        <svg
          className="de-svg"
          viewBox="34 22 298 242"
          preserveAspectRatio="xMidYMin meet"
          role="img"
          aria-label="Data estate coverage: one sector per data source, outer arc shows overall processed files"
        >
          <defs>
            {/* outer ring gradient: runs along the arc, left to right */}
            <linearGradient id="de-gauge-grad" gradientUnits="userSpaceOnUse" x1={CX - GAUGE_R} y1={CY} x2={CX + GAUGE_R} y2={CY}>
              <stop offset="0" stopColor="#16a34a" />
              <stop offset="0.55" stopColor="#10b981" />
              <stop offset="1" stopColor="#2dd4bf" />
            </linearGradient>
            <linearGradient id="de-gauge-grad-bar" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#16a34a" />
              <stop offset="1" stopColor="#2dd4bf" />
            </linearGradient>
          </defs>
          <path d={trackD} className={`de-gauge-track ${hovered === GAUGE_REST ? "highlight" : ""}`} />
          {doneD && (
            <path
              d={doneD}
              className={`de-gauge-done ${hovered === GAUGE_DONE ? "highlight" : ""}`}
              style={{ stroke: "url(#de-gauge-grad)" }}
            />
          )}
          {/* wide invisible hit areas so the thin arcs are easy to hover */}
          <path d={trackD} className="de-gauge-hit" onMouseEnter={() => setHovered(GAUGE_REST)} />
          {doneD && <path d={doneD} className="de-gauge-hit" onMouseEnter={() => setHovered(GAUGE_DONE)} />}

          <circle cx={CX} cy={CY} r={19} className="de-center-bg" />
          <image href="/DataTagIcons/DataEstateIcon.svg" x={CX - 10} y={CY - 10} width={20} height={20} />

          <text x={CX} y={CY + 60} textAnchor="middle" className="de-pct" pointerEvents="none">
            {fmtPct(shownPct)}
          </text>

          {sectors.map((s) => {
            const dim = hovered && hovered !== s.id;
            return (
              <g key={s.id} className="de-sector" style={{ opacity: dim ? 0.4 : 1 }}>
                <defs>
                  <linearGradient id={`de-d-${s.id}`} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor={s.doneGrad[0]} />
                    <stop offset="1" stopColor={s.doneGrad[1]} />
                  </linearGradient>
                  <linearGradient id={`de-r-${s.id}`} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor={s.restGrad[0]} />
                    <stop offset="1" stopColor={s.restGrad[1]} />
                  </linearGradient>
                </defs>
                <path
                  d={s.restD}
                  fill={`url(#de-r-${s.id})`}
                  onMouseEnter={() => setHovered(s.id)}
                />
                {s.doneD && (
                  <path
                    d={s.doneD}
                    fill={`url(#de-d-${s.id})`}
                    onMouseEnter={() => setHovered(s.id)}
                  />
                )}
              </g>
            );
          })}
        </svg>

        {onGauge && (
          <div className={`de-tip ${tipSide}`} role="status">
            <div className="de-tip-title">
              <span className="de-tip-icon" style={{ boxShadow: "inset 0 0 0 1.5px #10b981" }}>
                <img src="/DataTagIcons/DataEstateIcon.svg" alt="" />
              </span>
              <span className="de-tip-name">
                {hovered === GAUGE_DONE ? "Processed files" : "Remaining files"}
              </span>
            </div>
            <div className="de-tip-row">
              <span>{hovered === GAUGE_DONE ? "Processed" : "Remaining"}</span>
              <b>{numberFormatter(hovered === GAUGE_DONE ? allDone : all - allDone)}</b>
            </div>
            <div className="de-tip-row"><span>Total files</span><b>{numberFormatter(all)}</b></div>
            <div className="de-tip-row"><span>Processed</span><b>{fmtPct(overallPct)}</b></div>
            <div className="de-tip-bar">
              <span className="de-tip-track">
                <span
                  style={{
                    width: `${Math.max(overallPct, allDone ? 2 : 0)}%`,
                    background: "linear-gradient(90deg, #16a34a, #2dd4bf)",
                  }}
                />
              </span>
              <b>{fmtPct(overallPct)}</b>
            </div>
          </div>
        )}

        {current && (
          <div className={`de-tip ${tipSide}`} role="status">
            <div className="de-tip-title">
              <span className="de-tip-icon" style={{ boxShadow: `inset 0 0 0 1.5px ${current.color}` }}>
                {current.icon ? (
                  <img src={current.icon} alt="" />
                ) : (
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={current.color} strokeWidth="2.4" strokeLinejoin="round">
                    <path d="M4 4h6v6H4z M14 4h6v6h-6z M4 14h6v6H4z M14 14h6v6h-6z" />
                  </svg>
                )}
              </span>
              <span className="de-tip-name">{current.name}</span>
            </div>
            <div className="de-tip-row"><span>Total files</span><b>{numberFormatter(current.total)}</b></div>
            <div className="de-tip-row"><span>Share of all</span><b>{fmtPct(current.share)}</b></div>
            <div className="de-tip-row"><span>Processed</span><b>{numberFormatter(current.done)}</b></div>
            <div className="de-tip-row"><span>Remaining</span><b>{numberFormatter(current.total - current.done)}</b></div>
            <div className="de-tip-bar">
              <span className="de-tip-track">
                <span style={{ width: `${Math.max(current.pct, current.done ? 2 : 0)}%`, background: current.color }} />
              </span>
              <b>{fmtPct(current.pct)}</b>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}